// R1b-γ — Financial Billing tab.
// ERF Founder tenant is paywall-immune and shows a founder banner.
// Non-ERF tenants (future) would see real plan / entitlements / invoices.
// Online payments are not enabled (Stripe externally blocked).
import React, { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import StatusChip from "../../../components/StatusChip";

// Legal identifier — mirrors backend `TENANT_LEGAL_NAME` seeding.
const ERF_LEGAL_NAME = "ERF ENTERPRISES LLC";

export default function FinancialBillingTab() {
  const [tenant, setTenant] = useState(null);
  const [stripeAdapter, setStripeAdapter] = useState(null);

  useEffect(() => {
    api.get("/tenants/me").then((r) => setTenant(r.data)).catch(() => setTenant(null));
    api.get("/integrations").then((r) => {
      const s = (r.data || []).find((it) => it.key === "stripe");
      setStripeAdapter(s || null);
    }).catch(() => setStripeAdapter(null));
  }, []);

  const isFounder =
    tenant && (tenant.legal_name === ERF_LEGAL_NAME || tenant.is_founder === true);

  return (
    <div data-testid="financial-billing-body">
      <div className="mb-4">
        <div className="font-display font-semibold text-erf-dark text-lg">Billing</div>
        <div className="text-xs font-nav text-erf-dark/60 mt-0.5">Platform SaaS billing controls.</div>
      </div>

      {isFounder ? (
        <div
          className="rounded-md border border-erf-gold/50 bg-erf-gold/10 px-5 py-4"
          data-testid="founder-billing-banner"
        >
          <div className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover mb-1">
            Founder tenant · billing not applicable
          </div>
          <div className="font-display font-semibold text-erf-dark text-lg">
            {tenant?.display_name || "ERF Enterprises"} is the founder tenant.
          </div>
          <div className="mt-2 text-sm font-nav text-erf-dark/80 max-w-2xl">
            The founder tenant is <strong>paywall-immune</strong> by design. No subscription plan, no
            billing invoices, no payment methods are collected here. This invariant is enforced by the
            server: any attempt to attach a Stripe subscription to the founder tenant is rejected with
            a compliance audit-ledger entry.
          </div>
          <div className="mt-3 flex items-center gap-2 text-[11px] font-mono text-erf-dark/60">
            <span>Legal:</span> <span>{tenant?.legal_name}</span>
            <span>·</span>
            <span>Tenant id:</span> <span>{tenant?.id}</span>
          </div>
        </div>
      ) : (
        <div
          className="rounded-md border border-erf-borderCream bg-white p-5"
          data-testid="tenant-billing-panel"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/60 mb-1">Plan</div>
              <div className="font-display font-semibold text-erf-dark">— <span className="text-xs font-nav text-erf-dark/60">(not connected)</span></div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/60 mb-1">Next invoice</div>
              <div className="font-display font-semibold text-erf-dark">—</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/60 mb-1">Payment method</div>
              <div className="font-display font-semibold text-erf-dark">Not on file</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/60 mb-1">Provider</div>
              <div className="flex items-center gap-2">
                <span className="font-display font-semibold text-erf-dark">Stripe</span>
                <StatusChip status={stripeAdapter?.state || "externally_blocked"} />
              </div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-erf-borderCream text-[11px] font-nav text-erf-dark/75">
            Real billing activates when the Stripe adapter reaches <strong>Connected</strong> state via /admin/integrations.
          </div>
        </div>
      )}
    </div>
  );
}
